import { useEffect, useMemo, useState } from 'react'

import type { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  type SegmentedControlOption,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { CompactAlert } from '~/components/common/CompactAlert'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import { MasterTableActionBar } from '~/components/common/MasterTableActionBar'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { AreaFilter } from '~/components/common/filters/AreaFilter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { AREA_CODE_OPTIONS } from '~/models/area'
import { CreateSingularDayModal } from '~/pages/AdminSingularDay/components/CreateSingularDayModal'
import { DeleteSingularDayModal } from '~/pages/AdminSingularDay/components/DeleteSingularDayModal'
import { EditSingularDayModal } from '~/pages/AdminSingularDay/components/EditSingularDayModal'
import { GroupNamesModal } from '~/pages/AdminSingularDay/components/GroupNamesModal'
import { SingularDayTable } from '~/pages/AdminSingularDay/components/SingularDayTable'
import { useDeleteSingularDay } from '~/pages/AdminSingularDay/hooks/useDeleteSingularDay'
import {
  type SingularDayRow,
  convertListSingularDaysResponse,
  useListSingularDays,
} from '~/pages/AdminSingularDay/hooks/useListSingularDays'
import { usePersistedSingularDayFilters } from '~/pages/AdminSingularDay/hooks/usePersistedSingularDayFilters'
import { useSingularGroupNameMap } from '~/pages/AdminSingularDay/hooks/useSingularGroupNames'
import {
  defaultCreateDate,
  defaultFiscalYear,
  fiscalYearOf,
  fiscalYearOptionsFromDates,
} from '~/pages/AdminSingularDay/models/fiscalYear'
import {
  SINGULAR_DAY_CATEGORY_OPTIONS,
  matchesSingularDayAreaFilter,
  singularDayCategoryLabelKey,
} from '~/pages/AdminSingularDay/models/options'
import { mapSingularDayDeleteError } from '~/pages/AdminSingularDay/models/serverError'

type ModalState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; row: SingularDayRow }
  | { kind: 'delete'; row: SingularDayRow }

export const AdminSingularDayPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  // 年度 / エリア / カテゴリ は再訪をまたいで保持する (AC-SC-031 [P0])。
  // 保持対象と除外の理由は `models/persistedFilters.ts` の docstring 参照。
  const {
    fiscalYear,
    setFiscalYear,
    categoryFilter,
    setCategoryFilter,
    areaFilter,
    setAreaFilter,
  } = usePersistedSingularDayFilters()
  const { data, isLoading } = useListSingularDays(undefined)
  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteSingularDay()
  // 特異グループ表示名の取得失敗を明示する (PR #6541 reviewer 指摘対応):
  // 命名済みグループが取得失敗時に既定名表示へ fail-safe で潰れる挙動自体は
  // 維持しつつ (`SingularDayTable` / `SingularDayForm` が各自
  // `useSingularGroupNameMap` を呼ぶ)、 一覧ページはその失敗を Alert で
  // 可視化する (誤った業務事実を利用者に気づかせずに見せない)。
  const { isError: isGroupNamesError } = useSingularGroupNameMap()

  const [modalState, setModalState] = useState<ModalState>({ kind: 'none' })
  /**
   * 特異グループ名の一括エディタ (`GroupNamesModal`) の開閉。
   *
   * **`modalState` とは独立に持つ。** design
   * (`special-day-calendar.html:192-195`) はこの導線を**追加/編集モーダル内の
   * 需要扱い select 右端**に集約しており、modal-in-modal で重ねて開く。
   * `modalState` は排他な union なので、そこに混ぜると親モーダルが閉じてしまう。
   */
  const [isGroupNamesOpen, setGroupNamesOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined)
  const [overriddenAllAreaName, setOverriddenAllAreaName] = useState<
    string | undefined
  >(undefined)

  // 全特異日 (日付昇順、 backend が date ASC で返す)。
  const allRows = useMemo(
    () => (data !== undefined ? convertListSingularDaysResponse(data) : []),
    [data],
  )

  // 年度セレクタの選択肢 (登録済み日付から導出 + 昇順、 AC-MSD-001)。
  const fiscalYearOptions = useMemo(
    () => fiscalYearOptionsFromDates(allRows.map((row) => row.date)),
    [allRows],
  )

  // 登録済みデータから選択肢が消えた年度が選ばれたままになると、
  // セレクタ表示と一覧が食い違うため自動的に「すべて」に戻す。
  //
  // **取得完了までは判定しない。** `fiscalYearOptions` は `data` から導出するので、
  // ロード中は必ず空になる。ガードが無いと、localStorage から復元した年度
  // (AC-SC-031) が初回 mount のたびに「候補に無い」と誤判定されて消え、その
  // `undefined` が保存まで走って**保持が事実上効かなくなる**。
  useEffect(() => {
    if (data === undefined) return
    if (fiscalYear !== undefined && !fiscalYearOptions.includes(fiscalYear)) {
      setFiscalYear(undefined)
      setOverriddenAllAreaName(undefined)
    }
    // `setFiscalYear` は保持 hook が毎レンダー新しい関数を返すため依存に入れない
    // (入れると effect が毎レンダー走る)。判定の契機は data / 年度 / 候補の変化。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [data, fiscalYear, fiscalYearOptions])

  // 年度フィルタ適用後の行 (client 側絞り込み、 AC-MSD-003)。
  const yearFilteredRows = useMemo(
    () =>
      fiscalYear === undefined
        ? allRows
        : allRows.filter((row) => fiscalYearOf(row.date) === fiscalYear),
    [allRows, fiscalYear],
  )

  // カテゴリフィルタ用の件数 (選択年度内の全データで計算、 AC-10)。
  const categoryCounts = useMemo(() => {
    const counts = new Map<SingularDayCategory, number>()
    for (const row of yearFilteredRows) {
      counts.set(row.category, (counts.get(row.category) ?? 0) + 1)
    }
    return counts
  }, [yearFilteredRows])

  // カテゴリ + エリアフィルタ適用後の表示行 (client 側絞り込み、 AC-02/AC-10)。
  // 両フィルタは AND 条件。 エリアは「当該エリアを含む行 or 全エリア共通行
  // (areaCodes 空)」 を表示する (matchesSingularDayAreaFilter)。
  const visibleRows = useMemo(
    () =>
      yearFilteredRows.filter(
        (row) =>
          (categoryFilter === undefined || row.category === categoryFilter) &&
          matchesSingularDayAreaFilter(row.areaCodes, areaFilter),
      ),
    [yearFilteredRows, categoryFilter, areaFilter],
  )

  const handleCloseModal = (): void => {
    setModalState({ kind: 'none' })
    setDeleteError(undefined)
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (modalState.kind !== 'delete') {
      return
    }
    const { row } = modalState
    try {
      await deleteAsync({
        id: row.id,
        expectedUpdatedAt: row.updatedAt,
      })
      setModalState({ kind: 'none' })
      setDeleteError(undefined)
      setOverriddenAllAreaName(undefined)
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({ title: t('page:adminSingularDay.toast.deleted') })
    } catch (err) {
      setDeleteError(mapSingularDayDeleteError(err, t))
    }
  }

  // 年度フィルタの DS Select 選択肢。「すべて」は placeholder ではなく実 option
  // ('' value) として並べる (= AreaFilter.tsx 等、 既存 DS Select 採用箇所と揃える。
  // isClearable 前提の placeholder 復帰にはしない — native select 時代の
  // 「未選択 = すべて」を体現する option を再選択できる形を維持する)。
  const fiscalYearSelectOptions: DsSelectOptions = [
    {
      value: '',
      label: t('page:adminSingularDay.toolbar.fiscalYearAll'),
    },
    ...fiscalYearOptions.map((y) => ({
      value: String(y),
      label: t('page:adminSingularDay.toolbar.fiscalYearOption', {
        year: y,
      }),
    })),
  ]

  // カテゴリフィルタの segmented control 選択肢 (design: .ecl-segmented + 件数併記)。
  // value は '' (すべて) / SingularDayCategory の string 表現。
  const categorySegmentOptions: Array<SegmentedControlOption<string>> = [
    {
      value: '',
      label: t('page:adminSingularDay.filter.all', {
        count: yearFilteredRows.length,
      }),
    },
    ...SINGULAR_DAY_CATEGORY_OPTIONS.map((c) => ({
      value: String(c),
      label: `${t(singularDayCategoryLabelKey(c))} (${categoryCounts.get(c) ?? 0})`,
    })),
  ]

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:adminSingularDay.breadcrumb.admin') },
        { label: t('page:adminSingularDay.breadcrumb.page'), bold: true },
      ]}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* ページヘッダ (design .sdc-page-head): 説明 (左, 伸長) + 追加/CSV (右上)。
            追加/CSV 側は共通 MasterTableActionBar を使う (広域マスタ 8 タブと同型)。
            CSV 導線は遷移先の特異日 CSV (P1) を提供するまで onCsvImport / onCsvExport を
            未指定 = disabled にし、 押せない理由「この機能は準備中です」 をツールチップで出す
            (spec admin/master/singular-day-master.md AC-MSD-017)。
            flex-wrap は付けない (design css line 40 `.sdc-page-head` に無し)。
            wrap すると狭幅時にボタンが 2 行目に落ち、 justify-content: space-between
            の単独要素挙動で左寄せに退化する (= 「右寄りに配置」 が崩れる原因)。

            説明文は 8080 (`demand/special-day-calendar.html:103`) の
            `.page-description` と 1:1。 この画面が扱う対象 (暦とずれる日) と
            参照先 (需要想定 / 欠損補完) を `<b>` で立て、 2 文を `<br>` で割る
            (2026-07-14 にプロト側が長文から圧縮した後の版)。 太字は `as="b"` の
            局所強調のみで、 書体・色は PageIntro の `<Text>` から継承する。 */}
        <Flex alignItems="flex-start" justifyContent="space-between" gap={6}>
          {/* design `.sdc-page-head .page-description { flex: 1 1 auto }`。
              PageIntro は外側 margin / style props を持たないので置き場所側で伸ばす。 */}
          <Box flex="1 1 auto" minWidth="0">
            <PageIntro
              description={
                <>
                  <Text as="b" fontWeight="bold">
                    {t('page:adminSingularDay.description.line1Emphasis')}
                  </Text>
                  {t('page:adminSingularDay.description.line1Rest')}
                  <br />
                  {t('page:adminSingularDay.description.line2Prefix')}
                  <Text as="b" fontWeight="bold">
                    {t('page:adminSingularDay.description.line2DemandEmphasis')}
                  </Text>
                  {t('page:adminSingularDay.description.line2Conjunction')}
                  <Text as="b" fontWeight="bold">
                    {t(
                      'page:adminSingularDay.description.line2GapFillEmphasis',
                    )}
                  </Text>
                  {t('page:adminSingularDay.description.line2Suffix')}
                </>
              }
            />
          </Box>
          <Flex alignItems="center" flexShrink={0} gap={2}>
            {/* [8080 準拠 2026-09-17] 「特異グループ名を編集」 はここに置かない。
                design は追加/編集モーダル内の需要扱い select 右端**だけ**に
                集約している (`special-day-calendar.html:104` 「編集導線は
                modal 内 (需要扱い右端) のみに集約」)。旧実装は一世代前の
                design (同ファイル :129 の古いコメント「ヘッダーへ移動」) に
                従っていた。spec AC-MSD-011d は場所を縛っていない
                (「画面ヘッダー等から」) ので、この移動は spec と矛盾しない。 */}
            <MasterTableActionBar
              createLabel={t('page:adminSingularDay.toolbar.add')}
              csvExportPosition="after"
              embed
              onAddIndividual={() => {
                setModalState({ kind: 'create' })
                setOverriddenAllAreaName(undefined)
              }}
            />
          </Flex>
        </Flex>

        {/* フィルタ toolbar (design .ecl-toolbar 準拠。 共通 FilterToolbar が
            padding / border / gap を内部固定) */}
        <FilterToolbar>
          <FilterSection
            label={t('page:adminSingularDay.toolbar.fiscalYearLabel')}
          >
            {/* DS `Select` へ移行 (デザイナー指摘 2026-08-13)。 menu portal /
                font-size 補正は DsSelect 内部で自動適用される (DsSelect.tsx JSDoc)。
                「すべて」は isClearable 前提の placeholder ではなく実 option
                (value: '') として options に含める (AreaFilter.tsx 等の既存 DS
                Select 採用箇所と揃える判断。 native select 時代の
                「未選択 = すべて」を再選択可能な形のまま保つ)。 */}
            <DsSelect
              width="140px"
              aria-label={t('page:adminSingularDay.toolbar.fiscalYearLabel')}
              inputId="singularDay-fiscalYear"
              onChange={(v) => {
                setFiscalYear(enumSetValueAs(v))
                setOverriddenAllAreaName(undefined)
              }}
              options={fiscalYearSelectOptions}
              value={fiscalYear === undefined ? '' : String(fiscalYear)}
            />
          </FilterSection>

          {/* エリアフィルタ (AC-02): 未選択 = 全件、 特定エリア選択時は
              当該エリアを含む行 + 全エリア共通行を表示 */}
          <FilterSection label={t('page:adminSingularDay.filter.areaLabel')}>
            {/* 既存共通部品 `common/filters/AreaFilter` を使う。 `variant="select"` は
                「全エリア」 の実 option・`String(areaCode)` ⇄ `Number()` の往復・
                `selectFontFix`・menu の portal (一覧の sticky TH に覆われるのを防ぐ)
                を既に内包しており、 画面独自に組み直すと二重管理になる
                (レビュー指摘 2026-08-13)。 */}
            <AreaFilter
              width="160px"
              /* `allLabel` 既定の `common:area.all` は en 辞書に無く、 英語組織では
                 fallbackLng: 'ja' により「全エリア」と表示されてしまう (レビュー指摘
                 2026-08-13)。 移行前から使っていた画面固有キー (en: "All areas") を
                 明示して従来表示を維持する。 */
              allLabel={t('page:adminSingularDay.filter.areaAll')}
              areaCodes={AREA_CODE_OPTIONS}
              ariaLabel={t('page:adminSingularDay.filter.areaLabel')}
              onChange={(next) => {
                setAreaFilter(next)
                setOverriddenAllAreaName(undefined)
              }}
              value={areaFilter}
              variant="select"
            />
          </FilterSection>

          {/* 件数付きカテゴリフィルタ (AC-10) — design .ecl-segmented 準拠 */}
          <FilterSection label={t('page:adminSingularDay.table.category')}>
            <AccessibleSegmentedControl
              aria-label={t('page:adminSingularDay.table.category')}
              name="singularDay-category"
              onSelect={(next) => {
                if (next === '') {
                  setCategoryFilter(undefined)
                } else {
                  setCategoryFilter(Number(next) as SingularDayCategory)
                }
                setOverriddenAllAreaName(undefined)
              }}
              options={categorySegmentOptions}
              selected={
                categoryFilter === undefined ? '' : String(categoryFilter)
              }
            />
          </FilterSection>
        </FilterToolbar>

        {overriddenAllAreaName !== undefined && (
          <CompactAlert
            description={t('page:adminSingularDay.info.overriddenAllAreaDesc')}
            status="info"
            title={t('page:adminSingularDay.info.overriddenAllAreaTitle', {
              name: overriddenAllAreaName,
            })}
          />
        )}

        {/* 特異グループ表示名の取得失敗を明示する Alert (PR #6541 reviewer
            指摘対応)。 命名済みグループが取得失敗時に既定名表示へ潰れる
            (誤った業務事実) こと自体を利用者に気づかせる。 文言は
            GroupNamesModal / インライン命名欄と同じ既存キーを再利用する。 */}
        {isGroupNamesError && (
          <CompactAlert
            description={t('page:adminSingularDay.serverError.loadFailedDesc')}
            status="error"
            title={t('page:adminSingularDay.serverError.loadFailedTitle')}
          />
        )}

        {/* 一覧テーブル (AC-02/AC-03: 初期状態は空、 プリセット自動投入なし) */}
        <SingularDayTable
          isLoading={isLoading}
          onRowClick={(row) => {
            setModalState({ kind: 'edit', row })
            setOverriddenAllAreaName(undefined)
          }}
          rows={visibleRows}
        />

        {/* 登録モーダル (design AC-004: 日付入力可) */}
        {modalState.kind === 'create' && (
          <CreateSingularDayModal
            filterCategory={categoryFilter}
            initialDate={defaultCreateDate(fiscalYear ?? defaultFiscalYear())}
            isOpen
            onClose={handleCloseModal}
            onEditGroupNames={() => {
              setGroupNamesOpen(true)
            }}
            onInfo={setOverriddenAllAreaName}
          />
        )}

        {/* 編集モーダル (spec AC-007) */}
        {modalState.kind === 'edit' && (
          <EditSingularDayModal
            row={modalState.row}
            isOpen
            onClose={handleCloseModal}
            onDelete={(row) => {
              setDeleteError(undefined)
              setOverriddenAllAreaName(undefined)
              setModalState({ kind: 'delete', row })
            }}
            onEditGroupNames={() => {
              setGroupNamesOpen(true)
            }}
            onInfo={setOverriddenAllAreaName}
          />
        )}

        {/* 特異グループ名の一括編集モーダル (AC-MSD-011d)。
            groupNames は modal 自身が fetch する (GroupNamesModal の
            JSDoc「未ロードでの data loss」参照)。 */}
        {/* 追加/編集モーダルの上に重ねて開く (modal-in-modal)。 */}
        {isGroupNamesOpen && (
          <GroupNamesModal
            isOpen
            onClose={() => {
              setGroupNamesOpen(false)
            }}
          />
        )}

        {/* 削除確認ダイアログ (spec AC-009) */}
        {modalState.kind === 'delete' && (
          <DeleteSingularDayModal
            row={modalState.row}
            errorMessage={deleteError}
            isLoading={isDeleting}
            isOpen
            onCancel={handleCloseModal}
            onConfirm={() => {
              void handleDeleteConfirm()
            }}
          />
        )}
      </div>
    </PageLayout>
  )
}
